import {
  ActionIcon,
  Button,
  Card,
  Loader,
  Menu,
  Pagination,
  Progress,
  rem,
  Skeleton,
  Text,
  Tooltip,
  useComputedColorScheme,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { RunStatus } from '@prisma/client';
import {
  IconCancel,
  IconDotsVertical,
  IconExternalLink,
  IconMessage,
  IconPlayerPlayFilled,
  IconReportAnalytics,
} from '@tabler/icons-react';
import clsx from 'clsx';
import Link from 'next/link';
import router from 'next/router';
import { useEffect, useMemo } from 'react';
import { prettyDuration } from '~/common/time/duration';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { useProjectId } from '~/hooks/context/useProjectId';
import { useQueryParamNumeric } from '~/hooks/nav/useQueryParam';
import { useIntervalEffect } from '@react-hookz/web';
import { downloadFile } from '~/logic/file/download';
import { queryParamToString } from '~/logic/query-param';
import { api } from '~/trpc-extra/api';
import { GitBranchBadge } from '../common/GitBranchBadge';
import StatusIcon from '../icons/StatusIcon';
import { RunNoteHoverCard } from '../testRun/note/RunNoteHoverCard';
import TimeAgo from '../time/TimeAgo';
import ManualTriggerAdvanced from './ManualTriggerAdvanced';
import { match } from 'ts-pattern';

const ICON_SIZE = 20;
export const ROWS_DISPLAYED = 15;

export function TestSuiteResultView({
  onLatestRunStatusChange,
}: {
  onLatestRunStatusChange?: (status: RunStatus) => void;
}) {
  const [page, setPage] = useQueryParamNumeric('resultPage', 1);
  const trpcUtils = api.useUtils();

  const projectId = useProjectId();
  const testSuiteId = queryParamToString(router.query.testSuiteId);

  const { isAdmin } = useAuthData();

  const { data: runs, isPending: isRunsPending } = api.testSuite.getRuns.useQuery(
    { testSuiteId, take: ROWS_DISPLAYED, skip: (page - 1) * ROWS_DISPLAYED },
    {
      enabled: !!testSuiteId,
    },
  );

  useEffect(() => {
    if (runs) {
      const latestRunStatus = runs.testSuiteRuns.at(0)?.status;
      if (latestRunStatus) {
        onLatestRunStatusChange?.(latestRunStatus);
      }
    }
  }, [runs, onLatestRunStatusChange]);

  const { data: testSuite, isLoading: testSuiteLoading } = api.testSuite.get.useQuery({
    projectId: projectId!,
    testSuiteId,
  });

  const isRefreshEnabled = useMemo(
    () =>
      runs?.testSuiteRuns.some(
        (run) => run.status === 'RUNNING' || run.status === 'RETRYING' || run.status === 'QUEUED',
      ) ?? false,
    [runs?.testSuiteRuns],
  );
  useIntervalEffect(
    () => {
      trpcUtils.testSuite.getRuns.invalidate().catch(() => undefined);
    },
    isRefreshEnabled ? 10000 : undefined,
  );

  const { mutateAsync: clearSuiteRunHistory } = api.testSuite.clearRunHistory.useMutation();
  const { mutateAsync: cancelTestSuite } = api.testSuite.cancel.useMutation();
  const { mutateAsync: exportReport } = api.suiteRun.exportReport.useMutation();
  const totalSuiteRunsCount = runs?.totalSuiteRunsCount || 0;
  const recentTestSuiteRuns = runs?.testSuiteRuns || [];

  const totalPages = Math.ceil(totalSuiteRunsCount / ROWS_DISPLAYED);

  const colorScheme = useComputedColorScheme();

  return (
    isRunsPending ?
      <Skeleton
        height={70}
        className="my-4"
      />
    : !!recentTestSuiteRuns?.length ?
      <div className="mb-6 mt-4 flex flex-col gap-4">
        {(recentTestSuiteRuns || []).map(
          ({
            id,
            testRuns,
            createdAt,
            updatedAt,
            status,
            trigger,
            qualityCheckStatus,
            durationMs,
            runPhase,
            gitBranch,
            note,
          }) => {
            const {
              PASSED: passed = [],
              FLAKY: flaky = [],
              FAILED: failed = [],
              ERROR: errored = [],
              SKIPPED: skipped = [],
              RUNNING: running = [],
              QUEUED: queued = [],
              RETRYING: retrying = [],
            } = Object.groupBy(
              testRuns.filter((run) => run.status !== undefined && run.status !== null),
              ({ status }) => status,
            );
            const totalDurationMs = testRuns.reduce((acc, run) => {
              return acc + (run.durationMs ?? 0);
            }, 0);
            const numTestsRan = testRuns.length - skipped.length;

            const statusConfigs = [
              { status: RunStatus.PASSED, items: passed },
              { status: RunStatus.FLAKY, items: flaky },
              { status: RunStatus.FAILED, items: failed },
              { status: RunStatus.ERROR, items: errored },
              { status: RunStatus.RUNNING, items: running },
              { status: RunStatus.QUEUED, items: queued },
              { status: RunStatus.RETRYING, items: retrying },
              { status: RunStatus.SKIPPED, items: skipped },
            ];

            return (
              <Card
                key={id}
                withBorder
                className="text-sm"
              >
                <div className="grid grid-cols-12 gap-6">
                  {/* Column 1: Status and Execution Info */}
                  <div className="col-span-3 flex gap-3">
                    <div className="text-green">
                      <StatusIcon
                        status={status}
                        size={ICON_SIZE}
                        filled
                      />
                    </div>
                    <div className="flex flex-col">
                      <div className="flex flex-row items-center gap-2">
                        <Link
                          className="cursor-pointer font-bold hover:underline"
                          href={`/project/${projectId}/history/g_${id}`}
                        >
                          Execution #{(id || '')?.slice(0, 8)}
                        </Link>
                      </div>
                      <div className="mt-2 flex items-start gap-2">
                        {!!gitBranch && <GitBranchBadge gitBranch={gitBranch} />}
                        <div className="tm text-sm text-[var(--mantine-color-dimmed)]">
                          <div className="flex flex-row items-center gap-2">
                            <span>Triggered by {trigger} </span>
                            {!!note && (
                              <RunNoteHoverCard note={note}>
                                <IconMessage size={14} />
                              </RunNoteHoverCard>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="xl:col-span-0 col-span-1" />

                  {/* Column 2: Progress and Status */}
                  <div className="col-span-4">
                    {status === 'FAILED' && qualityCheckStatus === 'PENDING' ?
                      <div className="flex h-full items-center">
                        <span>Running quality check</span>
                        <Loader
                          className="ml-2"
                          color="#FFD43B"
                          size="sm"
                          type="dots"
                        />
                      </div>
                    : status === 'RUNNING' && testRuns.length === 0 ?
                      <div className="flex h-full items-center gap-2">
                        <Loader
                          color="#FFD43B"
                          size="sm"
                          type="dots"
                        />
                        <span className="text-[var(--mantine-color-dimmed)]">
                          {runPhase === 'SETUP' ?
                            'Running setup'
                          : runPhase === 'TEARDOWN' ?
                            'Running teardown'
                          : 'Starting run'}
                        </span>
                      </div>
                    : testRuns.length > 0 ?
                      <div className="flex h-full flex-col justify-center gap-1.5">
                        <div className="flex flex-row gap-1">
                          {[
                            passed.length > 0 && `${passed.length} passed`,
                            flaky.length > 0 && `${flaky.length} flaky`,
                            failed.length > 0 && `${failed.length} failed`,
                            skipped.length > 0 && `${skipped.length} skipped`,
                          ]
                            .filter(Boolean)
                            .join(', ')}
                          <span className="ml-1 opacity-60">
                            •&nbsp;
                            {status === 'RUNNING' ?
                              runPhase === 'SETUP' ?
                                'Running setup'
                              : runPhase === 'TEARDOWN' ?
                                'Running teardown'
                              : [
                                  running.length > 0 && `${running.length} running`,
                                  retrying.length > 0 && `${retrying.length} retrying`,
                                  queued.length > 0 && `${queued.length} queued`,
                                ]
                                  .filter(Boolean)
                                  .join(', ')

                            : `${numTestsRan} test${numTestsRan === 1 ? '' : 's'} ran`}
                          </span>
                        </div>
                        <Progress.Root
                          className={clsx('h-3 w-full', {
                            'animate-pulse': running.length > 0 || retrying.length > 0,
                          })}
                          size="xl"
                          radius="xl"
                          classNames={{
                            label: 'font-semibold text-[8px]',
                          }}
                        >
                          {statusConfigs
                            .filter(({ items }) => items.length > 0)
                            .map(({ items, status }) => (
                              <ProgressSection
                                key={status}
                                label={status}
                                color={getStatusColor({ status, colorScheme })}
                                value={items.length}
                                total={testRuns.length}
                              />
                            ))}
                        </Progress.Root>
                      </div>
                    : <div className="flex h-full items-center text-[var(--mantine-color-dimmed)]">No tests ran</div>}
                  </div>

                  {/* Column 3: Spacer */}
                  <div className="col-span-1 xl:col-span-2" />

                  {/* Column 4: Time Info */}
                  <div className="col-span-2 flex flex-col items-start justify-center text-[var(--mantine-color-dimmed)] xl:col-span-1">
                    <TimeAgo
                      time={createdAt}
                      textStyle="font-medium"
                    />
                    <div className="mt-1 flex flex-row gap-1">
                      <div>Duration:</div>
                      <div>
                        {updatedAt.getTime() === createdAt.getTime() || status === 'RUNNING' ?
                          <span>N/A</span>
                        : prettyDuration(Math.ceil((durationMs ?? totalDurationMs) / 1000))}
                      </div>
                    </div>
                  </div>

                  {/* Column 4: Action Menu */}
                  <div className="col-span-1 flex items-center justify-end">
                    <Menu
                      width={200}
                      shadow="md"
                      position="bottom-end"
                    >
                      <Menu.Target>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                        >
                          <IconDotsVertical size={ICON_SIZE} />
                        </ActionIcon>
                      </Menu.Target>

                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconExternalLink size={ICON_SIZE} />}
                          onClick={() => window.open(`/project/${projectId}/history/g_${id}`)}
                        >
                          Open
                        </Menu.Item>
                        {status === 'RUNNING' && (
                          <Menu.Item
                            leftSection={
                              <IconCancel
                                size={ICON_SIZE}
                                className="text-red-500"
                              />
                            }
                            onClick={() => {
                              modals.openConfirmModal({
                                title: 'Please confirm your action',
                                children: <Text size="sm">Cancel this run?</Text>,
                                labels: { confirm: 'Yes', cancel: 'No' },
                                confirmProps: { color: 'red' },
                                onConfirm: async () => {
                                  notifications.show({ message: 'Cancelling run...' });
                                  try {
                                    await cancelTestSuite({ testSuiteRunId: id });
                                    notifications.show({ message: 'Run cancelled' });
                                  } catch (e) {
                                    notifications.show({ message: 'Failed to cancel run', color: 'red' });
                                    console.error(e);
                                  }
                                  await trpcUtils.testSuite.getRuns.invalidate().catch(() => undefined);
                                },
                              });
                            }}
                          >
                            Cancel
                          </Menu.Item>
                        )}
                        <Menu.Item
                          leftSection={<IconReportAnalytics size={ICON_SIZE} />}
                          onClick={async () => {
                            try {
                              const result = await exportReport({ runId: id });
                              downloadFile(result.content, { filename: result.filename });
                              notifications.show({ message: 'Report downloaded successfully' });
                            } catch (error) {
                              notifications.show({
                                message: 'Failed to download report',
                                color: 'red',
                              });
                            }
                          }}
                        >
                          Download Report
                        </Menu.Item>

                        {isAdmin && projectId ?
                          <Menu.Item
                            color="orange"
                            leftSection={<IconExternalLink size={ICON_SIZE} />}
                            onClick={async () => {
                              await clearSuiteRunHistory({
                                projectId,
                                testSuiteId,
                                testSuiteRunId: id,
                              });
                              trpcUtils.testSuite.get.invalidate().catch(() => undefined);
                            }}
                          >
                            Delete
                          </Menu.Item>
                        : null}
                      </Menu.Dropdown>
                    </Menu>
                  </div>
                </div>
              </Card>
            );
          },
        )}
        <div className="mt-4 flex justify-center">
          <Pagination
            total={totalPages}
            defaultValue={page}
            onChange={setPage}
          />
        </div>
      </div>
    : testSuiteLoading ?
      <div className="my-6 flex justify-center">
        <Loader size="sm" />
      </div>
    : <div className="my-6 flex flex-col items-start gap-2">
        <div className="text-sm text-gray-500">
          You haven&apos;t run this test suite yet. Run it now to see the results.
        </div>

        <div className="text-sm text-gray-500">
          {testSuite?.suiteTests.length ?
            <Button
              leftSection={<IconPlayerPlayFilled style={{ width: rem(14), height: rem(14) }} />}
              variant="outline"
              onClick={() => {
                modals.open({
                  title: 'Manual Trigger',
                  size: '60%',
                  children: <ManualTriggerAdvanced />,
                });
              }}
            >
              Run Suite
            </Button>
          : null}
        </div>
      </div>
  );
}

export function ProgressSection({
  label,
  color,
  value,
  total,
}: {
  label: string;
  color: string;
  value: number;
  total: number;
}) {
  const progress = (value / total) * 100;
  return (
    <Tooltip
      className="capitalize"
      label={`${value} ${label.toLowerCase()}`}
    >
      <Progress.Section
        value={progress}
        color={color}
      />
    </Tooltip>
  );
}

export const getStatusColor = ({ status, colorScheme }: { status: RunStatus; colorScheme?: 'light' | 'dark' }) => {
  return match(status)
    .with('PASSED', () => 'green.5')
    .with('FLAKY', () => 'green.9')
    .with('FAILED', () => 'red.6')
    .with('ERROR', () => 'red.9')
    .with('RUNNING', () => 'yellow.7')
    .with('RETRYING', () => 'yellow.9')
    .with('QUEUED', () => (colorScheme === 'dark' ? 'gray.6' : 'gray'))
    .with('CANCELLED', 'SKIPPED', () => (colorScheme === 'light' ? 'gray.6' : 'gray'))
    .exhaustive();
};
